import Icon from '@/components/ui/AppIcon';

interface SecurityFeature {
  title: string;
  description: string;
  icon: string;
}

const SecuritySection = () => {
  const securityFeatures: SecurityFeature[] = [
    {
      title: "Bank-Level Encryption",
      description: "256-bit SSL encryption protects all your data in transit and at rest",
      icon: "LockClosedIcon"
    },
    {
      title: "SOC 2 Compliant",
      description: "Independently audited security controls meeting industry standards",
      icon: "ShieldCheckIcon"
    },
    {
      title: "Biometric Authentication",
      description: "Face ID and fingerprint login for secure mobile access",
      icon: "FingerPrintIcon"
    },
    {
      title: "Two-Factor Authentication",
      description: "Optional 2FA adds an extra layer of account protection",
      icon: "KeyIcon"
    },
    {
      title: "Secure Data Storage",
      description: "Your personal information is encrypted and never shared",
      icon: "ServerIcon"
    },
    {
      title: "Regular Security Audits",
      description: "Continuous monitoring and testing by cybersecurity experts",
      icon: "EyeIcon"
    }
  ];

  const certifications = [
    { name: "SSL Certified", icon: "LockClosedIcon" },
    { name: "SOC 2 Type II", icon: "ShieldCheckIcon" },
    { name: "GDPR Compliant", icon: "DocumentCheckIcon" },
    { name: "ISO 27001", icon: "CheckBadgeIcon" }
  ];

  return (
    <section className="py-20 px-4 lg:px-8 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 border border-success/20 rounded-full mb-6">
            <Icon name="ShieldCheckIcon" size={20} className="text-success" />
            <span className="text-sm font-medium text-success">Your Security is Our Priority</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Enterprise-Grade Security
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Trade with confidence knowing your data and funds are protected by industry-leading security measures
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {securityFeatures.map((feature, index) => (
            <div key={index} className="bg-card border border-border rounded-2xl p-6 hover:shadow-cyan-glow transition-all duration-200">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4">
                <Icon name={feature.icon as any} size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-3">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-card via-background to-card border border-border rounded-2xl p-8 lg:p-12">
          <h3 className="text-2xl font-heading font-bold text-foreground mb-8 text-center">
            Trusted Certifications & Compliance
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {certifications.map((cert, index) => (
              <div key={index} className="flex flex-col items-center text-center p-6 bg-background rounded-xl border border-border">
                <div className="flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                  <Icon name={cert.icon as any} size={32} className="text-success" />
                </div>
                <span className="text-foreground font-heading font-semibold">{cert.name}</span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-4">
              We never store your trading credentials or have access to your exchange accounts
            </p>
            <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground">
              <Icon name="InformationCircleIcon" size={16} className="text-primary" />
              <span>Signaly provides signals only - you maintain full control of your funds</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;